// src/components/DocumentAccessControl.js
import React, { useState, useEffect } from 'react';
import { usePermissions } from '../contexts/PermissionsContext';
import { User, UserPlus, X, Check, Shield } from 'lucide-react';

const DocumentAccessControl = ({ documentId, documentName }) => {
  const { getDocumentUsers, grantDocumentPermission, revokeDocumentPermission, PERMISSIONS } = usePermissions();
  const [users, setUsers] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [selectedPermission, setSelectedPermission] = useState(PERMISSIONS.VIEW);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchUsers = async () => {
      const documentUsers = await getDocumentUsers(documentId);
      setUsers(documentUsers);
      setIsLoading(false);
    };
    
    fetchUsers();
  }, [documentId, getDocumentUsers]);
  
  const handleAddUser = async () => {
    if (!newEmail) return;
    
    // In a real app, you would:
    // 1. Check if the user exists in your system
    // 2. Get their user ID
    // 3. Grant them the selected permission
    
    const mockNewUser = {
      id: `user-${Date.now()}`,
      name: newEmail.split('@')[0], // Just for demo
      email: newEmail,
      permissions: [selectedPermission],
    };
    
    await grantDocumentPermission(documentId, mockNewUser.id, selectedPermission);
    setUsers([...users, mockNewUser]);
    setNewEmail('');
  };
  
  const handleRevokePermission = async (userId, permission) => {
    await revokeDocumentPermission(documentId, userId, permission);
    
    // Update the local state
    setUsers(users.map(user => {
      if (user.id === userId) {
        return {
          ...user,
          permissions: user.permissions.filter(p => p !== permission)
        };
      }
      return user;
    }));
  };
  
  const handleGrantPermission = async (userId, permission) => {
    await grantDocumentPermission(documentId, userId, permission);
    
    // Update the local state
    setUsers(users.map(user => {
      if (user.id === userId) {
        return {
          ...user,
          permissions: [...new Set([...user.permissions, permission])]
        };
      }
      return user;
    }));
  };
  
  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold flex items-center">
          <Shield className="text-blue-500 mr-2" size={20} />
          Access Control for {documentName}
        </h2>
      </div>
      
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-3">Add people</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Email address"
            className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
          />
          <select
            value={selectedPermission}
            onChange={(e) => setSelectedPermission(e.target.value)}
            className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-md focus:outline-none focus:border-blue-500"
          >
            <option value={PERMISSIONS.VIEW}>Can view</option>
            <option value={PERMISSIONS.COMMENT}>Can comment</option>
            <option value={PERMISSIONS.SIGN}>Can sign</option>
            <option value={PERMISSIONS.EDIT}>Can edit</option>
            <option value={PERMISSIONS.SHARE}>Can share</option>
          </select>
          <button
            onClick={handleAddUser}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 transition-colors flex items-center"
          >
            <UserPlus size={18} className="mr-2" /> Add
          </button>
        </div>
      </div>
      
      <div>
        <h3 className="text-lg font-medium mb-3">People with access</h3>
        {isLoading ? (
          <div className="flex justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            No users have access to this document yet
          </div>
        ) : (
          <div className="space-y-3">
            {users.map((user) => (
              <div key={user.id} className="bg-gray-700 rounded-md p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center mb-3 sm:mb-0">
                  <div className="bg-gray-600 rounded-full p-2 mr-3">
                    <User size={18} className="text-gray-300" />
                  </div>
                  <div>
                    <div className="font-medium">{user.name}</div>
                    <div className="text-sm text-gray-400">{user.email}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.values(PERMISSIONS).map((permission) => {
                    const hasPermission = user.permissions.includes(permission);
                    return (
                      <button
                        key={permission}
                        onClick={() => hasPermission
                          ? handleRevokePermission(user.id, permission)
                          : handleGrantPermission(user.id, permission)
                        }
                        className={`px-3 py-1 text-xs rounded-full flex items-center ${
                          hasPermission
                            ? 'bg-blue-500 bg-opacity-20 text-blue-400'
                            : 'bg-gray-600 text-gray-400'
                        }`}
                      >
                        {hasPermission ? (
                          <Check size={12} className="mr-1" />
                        ) : null}
                        {permission.charAt(0).toUpperCase() + permission.slice(1)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentAccessControl;